import Button from "components/common/Button/Button";
import Text from "components/common/Text/Text";
import { IConfiguration } from "components/Gates/ConfigurationLoaderGate";
import { History } from "history";
import { getIdentity } from "init/tracking";
import * as React from "react";
import { Link } from "react-router-dom";
import { AccessToken, create, OAuthClient, Token } from "simple-oauth2";
import styled from "styled-components";
import delay from "utils/delay";
import rollBar from "utils/rollbar";
import uuidv1 from "uuid/v1";
import version from "version";

const Annotation = styled(Text)`
  position: fixed;
  bottom: 20px;
  right: 20px;
`;

const StyledButtonContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  width: 250px;
  margin: 20px 0px;
`;

export interface IAccess {
  fetchAccessToken(): Promise<string | undefined>;
  revokeAccessToken(): void;
  logTabletId(tabletId: string): void;
}

export interface IProps {
  className?: string;

  history: History;
  config: IConfiguration;
  children: any;
}

export const ActivationGateContext = React.createContext<IAccess | undefined>(
  undefined
);

export interface IState {
  loading: boolean;
  activated: boolean;
  tabletId?: string;
}

class ActivationGate extends React.Component<IProps, IState> {
  private oAuth2: OAuthClient;
  private onGoingRefreshRequest: Promise<AccessToken> | undefined;

  public constructor(props: IProps) {
    super(props);
    this.state = {
      loading: true,
      activated: false,
      tabletId: localStorage.getItem("loggedTabletId") || ""
    };

    this.oAuth2 = create({
      client: {
        id: props.config.auth_hydra_client_id,
        secret: props.config.auth_hydra_client_secret
      },
      auth: {
        tokenHost: props.config.auth_hydra_url,
        authorizePath: "/oauth2/auth",
        tokenPath: "/oauth2/token"
      }
    });
  }

  public render() {
    const { className, children } = this.props;
    const { loading, activated } = this.state;
    return (
      <div className={className}>
        {!loading && !activated && (
          <StyledButtonContainer>
            <StyledButton primary onClick={this.activate}>
              Activate
            </StyledButton>
            <Link to="/manage">
              <StyledButton secondary>Manual Activation</StyledButton>
            </Link>
            <Annotation>{version}</Annotation>
          </StyledButtonContainer>
        )}
        {!loading && activated && (
          <ActivationGateContext.Provider
            value={{
              fetchAccessToken: this.provideAccessToken,
              revokeAccessToken: this.revokeAccessToken,
              logTabletId: this.logTabletId
            }}
          >
            {children}
          </ActivationGateContext.Provider>
        )}
      </div>
    );
  }

  public async componentDidMount() {
    // console.debug("Fetching access token from query, then cache");
    const accessToken =
      (await this.fetchAccessTokenFromQuery()) ||
      this.fetchAccessTokenFromCache();

    // console.debug("Waiting 3s");
    // await delay(3000);
    this.setState({
      loading: false,
      activated: !!accessToken
    });
  }

  private logTabletId = (tabletId: string) => {
    this.setState({
      tabletId
    });
  };

  private fetchAccessTokenFromQuery = async (): Promise<
    AccessToken | undefined
  > => {
    const url = new URL(window.location.href);
    // console.debug("Extracting access code from url:", url);
    const accessCode = url.searchParams.get("code") || "";
    // console.debug("Extracted access code:", accessCode);
    sessionStorage.removeItem("tabletId");
    if (accessCode && accessCode !== "") {
      const token = await this.oAuth2.authorizationCode.getToken({
        code: accessCode,
        redirect_uri: this.props.config.auth_redirect_url
      });
      const accessToken = this.oAuth2.accessToken.create(token);
      this.saveTokenToCache(accessToken.token);
      return accessToken;
    }
    return undefined;
  };

  private saveTokenToCache = (token: Token) => {
    console.debug("Saving token to cache:", token);
    // rollBar.debug(
    //   `${identity} - ${this.state.tabletId ||
    //     "not activated"} - saving refresh token at ${new Date().toISOString()} : ${token.refresh_token.substring(
    //     0,
    //     8
    //   )}`
    // );
    localStorage.setItem("accessToken", JSON.stringify(token));
  };

  private removeTokenFromCache = () => {
    console.debug("Deleting access token from cache");
    // rollBar.debug(
    //   `${identity} - ${this.state.tabletId ||
    //     "not activated"} - removing token from cache at ${new Date().toISOString()}`
    // );
    localStorage.removeItem("accessToken");
  };

  private fetchAccessTokenFromCache = (): AccessToken | undefined => {
    // console.debug("Fetching access token from localStorage");
    const token = localStorage.getItem("accessToken");
    // console.debug("Token value in localStorage", token);

    if (token) {
      return this.oAuth2.accessToken.create(JSON.parse(token));
    }

    console.debug("No token found in localStorage");
    return undefined;
  };

  private shouldUpdateAccessToken = (
    currentToken: AccessToken | undefined,
    newToken: AccessToken | undefined
  ): boolean => {
    return (
      !!(!currentToken && newToken) ||
      !!(currentToken && !newToken) ||
      (currentToken && newToken
        ? currentToken.token.access_token !== newToken.token.access_token
        : false)
    );
  };

  private activate = () => {
    const tabletId = uuidv1();
    const authorizationUri = this.oAuth2.authorizationCode.authorizeURL({
      redirect_uri: this.props.config.auth_redirect_url,
      scope: "openid offline", // also can be an array of multiple scopes, ex. ['<scope1>, '<scope2>', '...']
      state: `${tabletId}`
    });
    sessionStorage.setItem("tabletId", tabletId);

    window.location.assign(authorizationUri);
  };

  private provideAccessToken = async (): Promise<string | undefined> => {
    // console.debug("Start ProvideAccessToken...");
    let accessToken = this.fetchAccessTokenFromCache();
    let accessTokenUpdated = false;

    if (accessToken && accessToken.expired()) {
      console.debug("Access Token found and is expired", accessToken);
      // rollBar.debug(
      //   `${identity} - ${this.state.tabletId ||
      //     "not activated"} - using refresh token : ${accessToken.token.refresh_token.substring(
      //     0,
      //     8
      //   )}`
      // );

      try {
        if (this.onGoingRefreshRequest === undefined) {
          console.debug(
            "onGoingRefreshRequest flag is undefined. Starting Token refresh"
          );
          this.onGoingRefreshRequest = accessToken.refresh();
        }
        accessToken = await this.onGoingRefreshRequest;
        console.debug("token refresh completed with result", accessToken);
        if (this.onGoingRefreshRequest) {
          this.onGoingRefreshRequest = undefined;
        }
      } catch (e) {
        console.debug("Exception occurred while refreshing token", e);
        accessToken = undefined;
        if (this.onGoingRefreshRequest) {
          this.onGoingRefreshRequest = undefined;
        }
        // rollBar.error(
        //   `${identity} - ${this.state.tabletId ||
        //     "not activated"} - error refreshing token ${e}`
        // );
      }

      if (accessToken && accessToken.expired()) {
        console.debug("Access Token after refresh is expired", accessToken);
        accessToken = undefined;
        // rollBar.error(
        //   `${identity}  - ${this.state.tabletId ||
        //     "not activated"} - refresh token failure`
        // );
      }
      accessTokenUpdated = true;
    }

    if (accessToken === undefined) {
      console.log("accessToken is undefined, loading from cache");
      const storedAccessToken = this.fetchAccessTokenFromCache();
      if (this.shouldUpdateAccessToken(accessToken, storedAccessToken)) {
        console.log(
          "shouldUpdateAccessToken returned true. Setting updated flag"
        );
        accessToken = storedAccessToken;
        accessTokenUpdated = true;
      }
    }

    if (accessTokenUpdated) {
      if (accessToken) {
        console.log(
          "Access token updated, and token is not null. Saving token to cache",
          accessToken
        );
        this.saveTokenToCache(accessToken.token);
      } else {
        console.log(
          "Access token updated but is null. Removing from cache",
          accessToken
        );
        this.removeTokenFromCache();
      }
      this.setState({
        activated: !!accessToken
      });
    }

    return accessToken ? accessToken.token.access_token : undefined;
  };

  private revokeAccessToken = () => {
    console.log("Revoking access token");
    this.setState(
      {
        activated: false
      },
      () => this.removeTokenFromCache()
    );
  };
}

export default ActivationGate;



// WEBPACK FOOTER //
// ./src/components/Gates/ActivationGate.tsx